import { createContext, useContext, useState, ReactNode } from 'react';

export type LookType = 'seitenschmied' | 'clean' | 'bold' | 'warm' | 'premium' | 'playful';

interface ThemeConfig {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    cardBg: string;
    text: string;
    textSecondary: string;
  };
  fonts: {
    heading: string;
    body: string;
  };
  buttonStyle: string;
  cardStyle: string;
  iconStyle: string;
}

interface TonalityConfig {
  hero: {
    headline: string;
    subheadline: string;
    primaryCTA: string;
    secondaryCTA: string;
  };
  trust: string[];
  pricing: {
    headline: string;
    subheadline: string;
    packages: Array<{
      name: string;
      price: string;
      features: string[];
      cta: string;
    }>;
  };
  finalCTA: {
    headline: string;
    button: string;
  };
}

const themeConfigs: Record<LookType, ThemeConfig> = {
  seitenschmied: {
    colors: {
      primary: '#1A1A1A',
      secondary: '#14b8a6',
      accent: '#10b981',
      background: '#FAFAFA',
      cardBg: '#FFFFFF',
      text: '#1A1A1A',
      textSecondary: '#6B6B6B',
    },
    fonts: {
      heading: "'Outfit', sans-serif",
      body: "'Inter', sans-serif",
    },
    buttonStyle: 'rounded-lg',
    cardStyle: 'rounded-xl border-2 border-gray-200 shadow-lg hover:shadow-2xl transition-shadow duration-300',
    iconStyle: 'stroke-2',
  },
  clean: {
    colors: {
      primary: '#2D3748',
      secondary: '#14b8a6',
      accent: '#10b981',
      background: '#FFFFFF',
      cardBg: '#F0FDFB',
      text: '#1A202C',
      textSecondary: '#718096',
    },
    fonts: {
      heading: "'Space Grotesk', sans-serif",
      body: "'Inter', sans-serif",
    },
    buttonStyle: 'rounded-lg',
    cardStyle: 'rounded-xl border border-teal-100',
    iconStyle: 'stroke-2',
  },
  bold: {
    colors: {
      primary: '#000000',
      secondary: '#FF6B35',
      accent: '#FFD23F',
      background: '#FFFFFF',
      cardBg: '#FAFAFA',
      text: '#000000',
      textSecondary: '#4A4A4A',
    },
    fonts: {
      heading: "'Space Grotesk', sans-serif",
      body: "'DM Sans', sans-serif",
    },
    buttonStyle: 'rounded-none',
    cardStyle: 'rounded-none border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]',
    iconStyle: 'stroke-[3]',
  },
  warm: {
    colors: {
      primary: '#8B4513',
      secondary: '#D2691E',
      accent: '#CD853F',
      background: '#FFF8F0',
      cardBg: '#FFFAF5',
      text: '#3E2723',
      textSecondary: '#6D4C41',
    },
    fonts: {
      heading: "'Merriweather', serif",
      body: "'Source Sans Pro', sans-serif",
    },
    buttonStyle: 'rounded-md',
    cardStyle: 'rounded-lg border-2 border-amber-200 shadow-lg',
    iconStyle: 'stroke-2',
  },
  premium: {
    colors: {
      primary: '#1A1A2E',
      secondary: '#16213E',
      accent: '#C9A961',
      background: '#FAFAFA',
      cardBg: '#FFFFFF',
      text: '#0F0F0F',
      textSecondary: '#5A5A5A',
    },
    fonts: {
      heading: "'Playfair Display', serif",
      body: "'Montserrat', sans-serif",
    },
    buttonStyle: 'rounded-sm',
    cardStyle: 'rounded-sm border border-gray-300 shadow-xl',
    iconStyle: 'stroke-1',
  },
  playful: {
    colors: {
      primary: '#6B46C1',
      secondary: '#ED64A6',
      accent: '#F6AD55',
      background: '#FFFBF5',
      cardBg: '#FFF5F7',
      text: '#2D3748',
      textSecondary: '#4A5568',
    },
    fonts: {
      heading: "'Poppins', sans-serif",
      body: "'Nunito', sans-serif",
    },
    buttonStyle: 'rounded-full',
    cardStyle: 'rounded-3xl border-2 border-purple-200 shadow-2xl',
    iconStyle: 'stroke-2',
  },
};

const tonalityConfigs: Record<LookType, TonalityConfig> = {
  seitenschmied: {
    hero: {
      headline: 'Websites, die wirken – Support, der bleibt',
      subheadline: 'Für Unternehmen aus der Region, die online überzeugen wollen.',
      primaryCTA: 'Kostenloses Erstgespräch',
      secondaryCTA: 'Pakete ansehen',
    },
    trust: ['Schnell umgesetzt', 'Rechtssicher optional', 'Persönlicher Support', 'Flexible Extras'],
    pricing: {
      headline: 'Transparente Pakete für jeden Bedarf',
      subheadline: 'Von kleinen Betrieben bis große Praxen – wählen Sie das passende Paket',
      packages: [
        {
          name: 'Essential',
          price: '699€ + 59€/Monat',
          features: [
            'Für kleine Läden mit Budget',
            'Website + Basis-Support',
            '2 Updates pro Jahr',
            'E-Mail-Support',
            '1 Extra inklusive',
            'SSL-Zertifikat'
          ],
          cta: 'Paket wählen'
        },
        {
          name: 'Professional',
          price: '999€ + 99€/Monat',
          features: [
            'Für Praxen, Kanzleien, Dienstleister',
            'Website + 1h Support/Monat',
            'Rechtstexte inklusive',
            'SEO-Optimierung',
            '1 Extra inklusive',
            'Impressum & Datenschutz',
            'E-Mail-Support'
          ],
          cta: 'Paket wählen'
        },
        {
          name: 'Enterprise',
          price: '1.499€ + 149€/Monat',
          features: [
            'Für große Praxen und Betriebe mit mehreren Dienstleistungen',
            'Website + 2h Support/Monat',
            'Quarterly Strategy Calls',
            'Premium SEO & Marketing',
            '1 Extra inklusive',
            'Rechtstexte inklusive',
            'Priority Support',
            'Individuelle Beratung'
          ],
          cta: 'Paket wählen'
        }
      ]
    },
    finalCTA: {
      headline: 'Bereit für Ihre professionelle Website?',
      button: 'Jetzt Erstgespräch vereinbaren',
    },
  },
  clean: {
    hero: {
      headline: 'Websites, die Kunden bringen – als Paket mit Support',
      subheadline: 'Wähle einen Look. Sieh, wie Design UND Tonalität sich verändern.',
      primaryCTA: 'Kostenloses Erstgespräch',
      secondaryCTA: 'Pakete vergleichen',
    },
    trust: ['Schnell online', 'Optionale Rechtssicherheit', 'Support inklusive', 'Extras flexibel'],
    pricing: {
      headline: 'Transparente Pakete',
      subheadline: 'Wähle das Paket, das zu deinem Business passt',
      packages: [
        {
          name: 'Essential',
          price: '699€ + 59€/Monat',
          features: [
            'Für kleine Läden mit Budget',
            'Website + Basis-Support',
            '2 Updates pro Jahr',
            'E-Mail-Support',
            '1 Extra inklusive',
            'SSL-Zertifikat'
          ],
          cta: 'Paket wählen'
        },
        {
          name: 'Professional',
          price: '999€ + 99€/Monat',
          features: [
            'Für Zahnärzte & Anwälte',
            'Website + 1h Support/Monat',
            'Rechtstexte inklusive',
            'SEO-Optimierung',
            '1 Extra inklusive',
            'Impressum & Datenschutz',
            'E-Mail-Support'
          ],
          cta: 'Paket wählen'
        },
        {
          name: 'Enterprise',
          price: '1.499€ + 149€/Monat',
          features: [
            'Für große Praxen & Strategie',
            'Website + 2h Support/Monat',
            'Quarterly Strategy Calls',
            'Premium SEO & Marketing',
            '1 Extra inklusive',
            'Rechtstexte inklusive',
            'Priority Support',
            'Individuelle Beratung'
          ],
          cta: 'Paket wählen'
        }
      ]
    },
    finalCTA: {
      headline: 'Sag mir Branche + Ziel – ich schlage das passende Paket vor',
      button: 'Kostenloses Erstgespräch sichern',
    },
  },
  bold: {
    hero: {
      headline: 'Websites, die verkaufen – Support inklusive',
      subheadline: 'Wechsle den Look. Erlebe, wie sich Design UND Sprache ändern.',
      primaryCTA: 'Jetzt durchstarten',
      secondaryCTA: 'Pakete checken',
    },
    trust: ['Blitzschnell live', 'Rechtssicher optional', 'Support dabei', 'Extras nach Wunsch'],
    pricing: {
      headline: 'Power-Pakete',
      subheadline: 'Wähle dein Level und leg los',
      packages: [
        {
          name: 'Essential',
          price: '699€ + 59€/Monat',
          features: [
            'Kleine Läden, Budget',
            'Website + Basis-Power',
            '2× Updates/Jahr',
            'E-Mail-Backup',
            '1 Extra dabei',
            'Sicher & Schnell'
          ],
          cta: 'Los geht\'s'
        },
        {
          name: 'Professional',
          price: '999€ + 99€/Monat',
          features: [
            'Zahnärzte & Anwälte',
            '1h Support jeden Monat',
            'Rechtstexte fertig',
            'Google-Ready SEO',
            '1 Extra dabei',
            'Datenschutz-konform',
            'Schnelle Hilfe'
          ],
          cta: 'Jetzt starten'
        },
        {
          name: 'Enterprise',
          price: '1.499€ + 149€/Monat',
          features: [
            'Große Praxen & Strategie',
            '2h Support/Monat',
            'Quarterly Strategy Calls',
            'Premium Marketing',
            '1 Extra dabei',
            'Rechtstexte inklusive',
            'VIP-Support',
            'Individuelle Power'
          ],
          cta: 'Durchstarten'
        }
      ]
    },
    finalCTA: {
      headline: 'Sag mir, was du brauchst – ich zeige dir den Weg',
      button: 'Lass uns loslegen',
    },
  },
  warm: {
    hero: {
      headline: 'Websites mit Herz – persönlicher Support inklusive',
      subheadline: 'Probiere verschiedene Looks. Spüre, wie sich Stil UND Ansprache wandeln.',
      primaryCTA: 'Gemeinsam starten',
      secondaryCTA: 'Angebote ansehen',
    },
    trust: ['Zügig umgesetzt', 'Rechtssicherheit möglich', 'Betreuung dabei', 'Extras wählbar'],
    pricing: {
      headline: 'Faire Angebote',
      subheadline: 'Für kleine Läden mit Budget bis große Praxen mit Strategie',
      packages: [
        {
          name: 'Essential',
          price: '699€ + 59€/Monat',
          features: [
            'Für kleine Läden',
            'Website + Betreuung',
            '2 Updates im Jahr',
            'E-Mail-Unterstützung',
            '1 Extra dabei',
            'Sicher verschlüsselt'
          ],
          cta: 'Anfragen'
        },
        {
          name: 'Professional',
          price: '999€ + 99€/Monat',
          features: [
            'Für Zahnärzte & Anwälte',
            '1 Stunde Support/Monat',
            'Rechtstexte fertig',
            'Suchmaschinen-fit',
            '1 Extra dabei',
            'Datenschutz-konform',
            'Persönliche Begleitung'
          ],
          cta: 'Beraten lassen'
        },
        {
          name: 'Enterprise',
          price: '1.499€ + 149€/Monat',
          features: [
            'Für große Praxen',
            '2 Stunden Support/Monat',
            'Vierteljährliche Gespräche',
            'Premium Marketing',
            '1 Extra dabei',
            'Rechtstexte inklusive',
            'Vorrang-Support',
            'Langfristige Pflege'
          ],
          cta: 'Gespräch vereinbaren'
        }
      ]
    },
    finalCTA: {
      headline: 'Erzähl mir von deinem Betrieb – ich berate dich gerne',
      button: 'Unverbindlich Kontakt aufnehmen',
    },
  },
  premium: {
    hero: {
      headline: 'Exzellente Websites – mit erstklassigem Support',
      subheadline: 'Erkunden Sie verschiedene Looks. Beobachten Sie, wie sich Ästhetik UND Tonalität verändern.',
      primaryCTA: 'Beratungstermin vereinbaren',
      secondaryCTA: 'Leistungspakete',
    },
    trust: ['Zeitnah realisiert', 'Rechtssicherheit optional', 'Premium-Support', 'Flexible Erweiterungen'],
    pricing: {
      headline: 'Pakete',
      subheadline: 'Exzellenz für anspruchsvolle Unternehmen',
      packages: [
        {
          name: 'Essential',
          price: '699€ + 59€/Monat',
          features: [
            'Kleine Betriebe',
            'Website + Basis-Service',
            '2 Updates jährlich',
            'E-Mail-Support',
            '1 Extra inklusive',
            'Enterprise-Security'
          ],
          cta: 'Auswählen'
        },
        {
          name: 'Professional',
          price: '999€ + 99€/Monat',
          features: [
            'Praxen & Kanzleien',
            '1h Support monatlich',
            'Rechtstexte inklusive',
            'SEO-Excellence',
            '1 Extra inklusive',
            'Compliance-Ready',
            'Persönlicher Service'
          ],
          cta: 'Auswählen'
        },
        {
          name: 'Enterprise',
          price: '1.499€ + 149€/Monat',
          features: [
            'Große Praxen & Strategie',
            '2h Support monatlich',
            'Quarterly Strategy Sessions',
            'Premium Marketing',
            '1 Extra inklusive',
            'Rechtstexte inklusive',
            'Priority Service',
            'Dedicated Consulting'
          ],
          cta: 'Auswählen'
        }
      ]
    },
    finalCTA: {
      headline: 'Teilen Sie uns Ihre Anforderungen mit – wir empfehlen die optimale Lösung',
      button: 'Exklusives Erstgespräch',
    },
  },
  playful: {
    hero: {
      headline: 'Websites, die rocken – Support, der begeistert',
      subheadline: 'Spiel mit den Looks. Entdecke, wie sich Style UND Vibe ändern.',
      primaryCTA: 'Let\'s talk!',
      secondaryCTA: 'Pakete entdecken',
    },
    trust: ['Ruckzuck online', 'Rechtssicher? Klar!', 'Support? Immer da!', 'Extras? Gerne!'],
    pricing: {
      headline: 'Coole Pakete 🎨',
      subheadline: 'Von Starter bis Rockstar – wir haben dein Paket',
      packages: [
        {
          name: 'Essential 🌱',
          price: '699€ + 59€/Monat',
          features: [
            'Kleine Läden, Budget',
            'Website + Support',
            '2× Updates/Jahr',
            'E-Mail-Hilfe',
            '1 Extra gratis',
            'Safe & Sound'
          ],
          cta: 'Let\'s go!'
        },
        {
          name: 'Professional 🚀',
          price: '999€ + 99€/Monat',
          features: [
            'Zahnärzte & Anwälte',
            '1h Support/Monat',
            'Rechtstexte? Check!',
            'Google Love',
            '1 Extra gratis',
            'DSGVO? Klar!',
            'Schnelle Hilfe'
          ],
          cta: 'Ich will das!'
        },
        {
          name: 'Enterprise 👑',
          price: '1.499€ + 149€/Monat',
          features: [
            'Große Praxen & Strategie',
            '2h Support/Monat',
            'Quarterly Calls',
            'Marketing Boost',
            '1 Extra gratis',
            'Rechtstexte dabei',
            'VIP-Support',
            'Premium Care'
          ],
          cta: 'All in!'
        }
      ]
    },
    finalCTA: {
      headline: 'Verrat mir deine Idee – ich zeig dir, was geht',
      button: 'Jetzt Gespräch buchen',
    },
  },
};

interface ThemeContextType {
  currentLook: LookType;
  setCurrentLook: (look: LookType) => void;
  theme: ThemeConfig;
  tonality: TonalityConfig;
  isTransitioning: boolean;
  showToast: boolean;
  toastMessage: string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const lookLabels: Record<LookType, string> = {
  seitenschmied: 'Seitenschmied Original',
  clean: 'Clean & Minimal',
  bold: 'Bold & Modern',
  warm: 'Warm & Handwerklich',
  premium: 'Premium & Elegant',
  playful: 'Playful & Kreativ',
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [currentLook, setCurrentLook] = useState<LookType>('seitenschmied');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleLookChange = (look: LookType) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentLook(look);
      setToastMessage(`Look aktiv: ${lookLabels[look]}`);
      setShowToast(true);
      setTimeout(() => setIsTransitioning(false), 300);
    }, 150);
  };

  const handleToastHide = () => {
    setShowToast(false);
  };

  return (
    <ThemeContext.Provider
      value={{
        currentLook,
        setCurrentLook: handleLookChange,
        theme: themeConfigs[currentLook],
        tonality: tonalityConfigs[currentLook],
        isTransitioning,
        showToast,
        toastMessage,
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Outfit:wght@500;600;700;800&family=Space+Grotesk:wght@500;600;700&family=DM+Sans:wght@400;500;600&family=Merriweather:wght@700;900&family=Source+Sans+Pro:wght@400;600&family=Playfair+Display:wght@600;700;800&family=Montserrat:wght@400;500;600&family=Poppins:wght@600;700;800&family=Nunito:wght@400;600;700&display=swap');
          
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}